import { useState, useRef, useEffect, useCallback } from 'react'

/* ====================================================================
   useInlineEdit — shared state machine for click-to-edit affordances.
   Used by:
     - SidebarItem (shipment rename)
     - RailField   (per-field rail edit, with locked-confirm flow)
     - Header      (header title rename)
   Each consumer renders its own visuals; they all share this hook for the
   same edit state machine: begin → draft → save (with optional locked
   confirmation step) → cancel.
   ==================================================================== */
export default function useInlineEdit({ value, locked, onCommit }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [pendingConfirm, setPendingConfirm] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      if (inputRef.current.select) inputRef.current.select()
    }
  }, [editing])

  const begin = useCallback(() => {
    setDraft(value ?? '')
    setPendingConfirm(false)
    setEditing(true)
  }, [value])

  const cancel = useCallback(() => {
    setEditing(false)
    setPendingConfirm(false)
    setDraft(value ?? '')
  }, [value])

  const attemptSave = useCallback(() => {
    if (locked) { setPendingConfirm(true); return }
    onCommit(draft, { notify: false })
    setEditing(false)
  }, [locked, onCommit, draft])

  const confirmSave = useCallback(() => {
    onCommit(draft, { notify: true })
    setEditing(false)
    setPendingConfirm(false)
  }, [onCommit, draft])

  const handleKey = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); attemptSave() }
    if (e.key === 'Escape') { e.preventDefault(); cancel() }
  }, [attemptSave, cancel])

  return {
    editing,
    draft,
    setDraft,
    pendingConfirm,
    inputRef,
    begin,
    cancel,
    attemptSave,
    confirmSave,
    handleKey
  }
}
